import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { THEME } from '../../../web/components-v2/Theme/theme';

const { colors, spacing } = THEME;

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'retrying';

interface SaveStatusProps {
	status: SaveStatus;
	variant?: 'inline' | 'simple' | 'detailed';
	retryCount?: number;
	errorMessage?: string | null;
	lastSavedAt?: Date | null;
	className?: string;
}

/**
 * Unified SaveStatus component for automation flows.
 * Supports three display variants:
 * - 'inline': Minimal text-only display
 * - 'simple': Text with basic styling
 * - 'detailed': Full display with icons and timestamps
 */
export function SaveStatus({
	status,
	variant = 'simple',
	retryCount = 0,
	errorMessage,
	lastSavedAt,
	className,
}: SaveStatusProps) {
	// Define getStatusText before any early returns to comply with rules of hooks
	const getStatusText = React.useCallback((): string => {
		switch (status) {
			case 'saving':
				return 'Saving...';
			case 'retrying':
				return `Retrying... (Attempt ${retryCount})`;
			case 'saved':
				if (variant === 'detailed') {
					return 'All changes saved';
				}
				return 'Saved';
			case 'error':
				return errorMessage || 'Failed to save';
			case 'idle':
				if (variant === 'detailed' && lastSavedAt) {
					const now = new Date();
					const diffMs = now.getTime() - lastSavedAt.getTime();
					const diffMinutes = Math.floor(diffMs / 60000);

					if (diffMinutes < 1) {
						return 'Saved just now';
					} else if (diffMinutes < 60) {
						return `Saved ${diffMinutes}m ago`;
					} else {
						const diffHours = Math.floor(diffMinutes / 60);
						return `Saved ${diffHours}h ago`;
					}
				}
				return '';
			default:
				return '';
		}
	}, [status, variant, retryCount, errorMessage, lastSavedAt]);

	// Don't show anything for idle status unless we have a lastSavedAt in detailed mode
	if (status === 'idle' && !(variant === 'detailed' && lastSavedAt)) {
		return null;
	}

	const getStatusIcon = () => {
		if (variant !== 'detailed') {
			return null;
		}

		switch (status) {
			case 'saving':
			case 'retrying':
				return (
					<svg width='16' height='16' viewBox='0 0 16 16' className={css(styleSheet.spinner)}>
						<circle cx='8' cy='8' r='6' stroke='currentColor' strokeWidth='2' fill='none' strokeDasharray='30' />
					</svg>
				);
			case 'saved':
			case 'idle': // When showing "Saved X ago"
				return (
					<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
						<circle cx='8' cy='8' r='7' fill={colors.success.main} />
						<path
							d='M5 8l2 2 4-4'
							stroke={colors.success.contrastText}
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						/>
					</svg>
				);
			case 'error':
				return (
					<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
						<circle cx='8' cy='8' r='7' fill={colors.danger.main} />
						<path d='M8 5v3m0 2h.01' stroke={colors.danger.contrastText} strokeWidth='2' strokeLinecap='round' />
					</svg>
				);
			default:
				return null;
		}
	};

	const getContainerStyles = () => {
		const baseStyles = [styleSheet.container];

		// Add variant-specific styles
		if (variant === 'inline') {
			baseStyles.push(styleSheet.inline);
		} else if (variant === 'detailed') {
			baseStyles.push(styleSheet.detailed);
		}

		// Add status-specific styles
		switch (status) {
			case 'saving':
			case 'retrying':
				baseStyles.push(styleSheet.saving);
				break;
			case 'saved':
			case 'idle': // When showing "Saved X ago"
				baseStyles.push(styleSheet.saved);
				break;
			case 'error':
				baseStyles.push(styleSheet.error);
				break;
			default:
				// No additional styles for unknown status
				break;
		}

		return css(...baseStyles);
	};

	const statusText = getStatusText();
	if (!statusText) {
		return null;
	}

	return (
		<div className={`${getContainerStyles()} ${className || ''}`} role='status' aria-live='polite' aria-atomic='true'>
			{getStatusIcon()}
			<span>{statusText}</span>
		</div>
	);
}

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		fontSize: 12,
		transition: 'all 0.2s ease',
	},
	inline: {
		// Minimal inline styles
		padding: 0,
		background: 'transparent',
	},
	detailed: {
		// Detailed variant with more padding
		padding: `${spacing.xs}px ${spacing.sm}px`,
		borderRadius: 6,
	},
	saving: {
		color: colors.info.main,
		backgroundColor: colors.info.light,
		border: `1px solid ${colors.info.main}33`,
	},
	saved: {
		color: colors.success.dark,
		backgroundColor: colors.success.light,
		border: `1px solid ${colors.success.main}33`,
	},
	error: {
		color: colors.danger.dark,
		backgroundColor: colors.danger.light,
		border: `1px solid ${colors.danger.main}33`,
	},
	spinner: {
		animation: 'spin 1s linear infinite',
		'@keyframes spin': {
			from: {
				transform: 'rotate(0deg)',
			},
			to: {
				transform: 'rotate(360deg)',
			},
		},
	},
});
